import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
  effect,
  DestroyRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SecondaryPanelService } from '@app/core/layout/secondary-panel.service';
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import { CreateUserService } from 'src/app/data/user/create-user.service';
import { InviteMembersService } from 'src/app/data/member/invite-members.service';
import { parseDateInput } from '@app/core/date/date-time.util';

/**
 * Component that renders the content of the secondary panel.
 * Displays different quick action forms based on the active action.
 * Phase 8: Changed from MatDrawer to adjacent panel (Gmail-style).
 */
@Component({
  selector: 'app-right-drawer-content',
  templateUrl: './right-drawer-content.component.html',
  styleUrls: ['./right-drawer-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS, MatProgressSpinnerModule],
})
export class RightDrawerContentComponent {
  private readonly fb = inject(FormBuilder);
  private readonly secondaryPanel = inject(SecondaryPanelService);
  private readonly layout = inject(LayoutBreakpointService);
  private readonly createUserService = inject(CreateUserService);
  private readonly inviteMembersService = inject(InviteMembersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly titleCreateUser = $localize`:@@home.homeNav.button.createUser:ユーザー作成`;
  private readonly titleInviteStaff = $localize`:@@home.homeNav.button.inviteStaff:スタッフ招待`;
  private readonly msgUserCreated = $localize`:@@home.rightDrawer.message.userCreated:ユーザーを作成しました`;
  private readonly msgUserCreateFailed = $localize`:@@home.rightDrawer.message.userCreateFailed:ユーザーの作成に失敗しました`;
  private readonly msgInviteSent = $localize`:@@home.rightDrawer.message.inviteSent:招待メールを送信しました`;
  private readonly msgInviteFailed = $localize`:@@home.rightDrawer.message.inviteFailed:招待の送信に失敗しました`;

  constructor() {
    // Reset forms when action changes
    effect(() => {
      const action = this.activeAction();
      if (action) {
        this.resetForms();
      }
    });
  }

  /** Currently active quick action */
  readonly activeAction = this.secondaryPanel.quickActionId;

  /** Section ID for the current action context */
  readonly sectionId = this.secondaryPanel.sectionId;

  /** Whether the panel is open */
  readonly isOpen = this.secondaryPanel.isQuickAction;

  /** Loading state */
  readonly isSubmitting = signal(false);

  /** Error message */
  readonly errorMessage = signal<string | null>(null);

  /** Success message */
  readonly successMessage = signal<string | null>(null);

  /** User input mode: 'code' or 'name' */
  readonly userInputMode = signal<'code' | 'name'>('name');

  /** Dynamic width based on device type */
  readonly panelWidth = computed(() => {
    if (this.layout.isHandset()) return '100%';
    if (this.layout.isTablet()) return '300px';
    return '320px';
  });

  /** Title for the panel header based on active action */
  readonly drawerTitle = computed(() => {
    const action = this.activeAction();
    switch (action) {
      case 'createUser': return this.titleCreateUser;
      case 'inviteStaff': return this.titleInviteStaff;
      default: return '';
    }
  });

  readonly closeAriaLabel = $localize`:@@home.rightDrawer.aria.close:閉じる`;

  /** User creation form */
  readonly userForm: FormGroup = this.fb.group({
    code: [''],
    lastName: [''],
    firstName: [''],
    birthDate: [''],
    sex: ['other'],
    contractStartAt: [this.getTodayStringJST(), [Validators.required]],
  });

  /** Staff invitation form */
  readonly inviteForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    accountManageRole: [false],
    userManageRole: [false],
  });

  /** Sex options */
  readonly sexOptions = [
    { value: 'male', label: $localize`:@@shared.sexLangPipe.sex.male:男性` },
    { value: 'female', label: $localize`:@@shared.sexLangPipe.sex.female:女性` },
    { value: 'other', label: $localize`:@@shared.sexLangPipe.sex.other:その他` },
  ];

  /** Toggle user input mode */
  toggleInputMode(): void {
    const current = this.userInputMode();
    const next = current === 'code' ? 'name' : 'code';
    this.userInputMode.set(next);

    // Clear fields and update validators
    if (next === 'code') {
      this.userForm.get('code')?.setValidators([Validators.required]);
      this.userForm.get('lastName')?.clearValidators();
      this.userForm.get('firstName')?.clearValidators();
      this.userForm.patchValue({ lastName: '', firstName: '' });
    } else {
      this.userForm.get('code')?.clearValidators();
      this.userForm.get('lastName')?.setValidators([Validators.required]);
      this.userForm.get('firstName')?.setValidators([Validators.required]);
      this.userForm.patchValue({ code: '' });
    }
    this.userForm.get('code')?.updateValueAndValidity();
    this.userForm.get('lastName')?.updateValueAndValidity();
    this.userForm.get('firstName')?.updateValueAndValidity();
  }

  /** Close the panel */
  close(): void {
    this.resetForms();
    this.secondaryPanel.close();
  }

  /** Reset all forms and messages */
  private resetForms(): void {
    this.userInputMode.set('name');
    this.userForm.reset({
      code: '',
      lastName: '',
      firstName: '',
      birthDate: '',
      sex: 'other',
      contractStartAt: this.getTodayStringJST(),
    });
    // Set initial validators for name mode
    this.userForm.get('code')?.clearValidators();
    this.userForm.get('lastName')?.setValidators([Validators.required]);
    this.userForm.get('firstName')?.setValidators([Validators.required]);
    this.userForm.get('code')?.updateValueAndValidity();
    this.userForm.get('lastName')?.updateValueAndValidity();
    this.userForm.get('firstName')?.updateValueAndValidity();

    this.inviteForm.reset({
      email: '',
      accountManageRole: false,
      userManageRole: false,
    });
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  /** Get today's date in YYYY/MM/DD format (Japan timezone) */
  private getTodayStringJST(): string {
    const now = new Date();
    // Convert to Japan timezone (UTC+9)
    const jstOffset = 9 * 60; // minutes
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const jst = new Date(utc + jstOffset * 60000);

    const year = jst.getFullYear();
    const month = String(jst.getMonth() + 1).padStart(2, '0');
    const day = String(jst.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  /** Convert YYYY/MM/DD to ISO 8601 format */
  private toISODate(dateStr: string): string {
    if (!dateStr) return '';
    const parsed = parseDateInput(dateStr);
    if (!parsed) {
      return '';
    }
    return parsed.toISOString();
  }

  /** Submit user creation form */
  submitUserForm(): void {
    if (this.userForm.invalid || this.isSubmitting()) return;

    const sectionId = this.sectionId();
    if (!sectionId) return;

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    const formValue = this.userForm.value;
    const isCodeMode = this.userInputMode() === 'code';

    // Parse birthDate, set to null if empty or invalid
    let birthDate: string | null = null;
    if (formValue.birthDate) {
      try {
        birthDate = this.toISODate(formValue.birthDate);
      } catch {
        birthDate = null;
      }
    }

    const userData = {
      accountEnabled: 'true',
      code: isCodeMode ? formValue.code : null,
      firstName: isCodeMode ? null : formValue.firstName,
      lastName: isCodeMode ? null : formValue.lastName,
      jobTitleCode: 'NU',
      birthDate: birthDate,
      sex: formValue.sex,
      contractStartAt: this.toISODate(formValue.contractStartAt),
    };

    // API expects an array of users
    const payload = [userData];

    this.createUserService.createUser(payload, sectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.successMessage.set(this.msgUserCreated);
          setTimeout(() => this.close(), 1500);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.errorMessage.set(
            err?.error?.message ||
            this.msgUserCreateFailed,
          );
        },
      });
  }

  /** Submit staff invitation form */
  submitInviteForm(): void {
    if (this.inviteForm.invalid || this.isSubmitting()) return;

    const sectionId = this.sectionId();
    if (!sectionId) return;

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    const formValue = this.inviteForm.value;
    const roles: string[] = ['MEASUREMENT', 'REFER'];
    if (formValue.accountManageRole) roles.push('ACCOUNT_MANAGEMENT');
    if (formValue.userManageRole) roles.push('USER_MANAGEMENT');

    const payload = {
      email: formValue.email,
      role: roles,
      jobTitleCode: 'ST',
      userType: 'STAFF',
      userId: null,
    };

    this.inviteMembersService.inviteMembers(sectionId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.successMessage.set(this.msgInviteSent);
          setTimeout(() => this.close(), 1500);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.errorMessage.set(
            err?.error?.message ||
            this.msgInviteFailed,
          );
        },
      });
  }

  /** Check if current form is valid */
  isFormValid(): boolean {
    const action = this.activeAction();
    if (action === 'createUser') return this.userForm.valid;
    if (action === 'inviteStaff') return this.inviteForm.valid;
    return false;
  }

  /** Submit current form */
  submitForm(): void {
    const action = this.activeAction();
    if (action === 'createUser') this.submitUserForm();
    if (action === 'inviteStaff') this.submitInviteForm();
  }
}
